import { useState, useEffect, useMemo, useRef, useCallback } from 'react'

export interface ComboboxOption {
  value: string
  label: string
}

const defaultInputClasses =
  'w-full px-3 py-1.5 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-[var(--text-primary)] text-sm placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
  allowFreeText = false,
  inputClassName = '',
  onSearch,
  searching = false,
}: {
  options: ComboboxOption[]
  value: string
  onChange: (value: string, label?: string) => void
  placeholder?: string
  className?: string
  allowFreeText?: boolean
  inputClassName?: string
  onSearch?: (query: string) => void
  searching?: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  // Derive display text from the current value
  const displayLabel = useMemo(() => {
    if (!value) return ''
    const opt = options.find((o) => o.value === value)
    return opt ? opt.label : value
  }, [value, options])

  // When value changes externally, sync search text
  useEffect(() => {
    if (!isOpen) {
      setSearch(displayLabel)
    }
  }, [displayLabel, isOpen])

  // Debounced server search
  const debouncedSearch = useCallback((query: string) => {
    if (!onSearch) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => onSearch(query), 250)
  }, [onSearch])

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [])

  // Filter options based on search text (client-side only when no onSearch)
  const filtered = useMemo(() => {
    if (onSearch) return options // server already filtered
    if (!search.trim()) return options
    const term = search.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(term))
  }, [options, search, onSearch])

  // Click-outside handler
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        // If free text allowed, commit typed text; otherwise revert to current value label
        if (allowFreeText && search.trim()) {
          const exactMatch = options.find((o) => o.label.toLowerCase() === search.toLowerCase())
          if (exactMatch) {
            onChange(exactMatch.value, exactMatch.label)
          } else {
            onChange(search.trim(), search.trim())
          }
        } else {
          setSearch(displayLabel)
        }
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [allowFreeText, search, displayLabel, options, onChange])

  const handleSelect = (opt: ComboboxOption) => {
    onChange(opt.value, opt.label)
    setSearch(opt.label)
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearch(displayLabel)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered.length > 0) {
        handleSelect(filtered[0])
      } else if (allowFreeText && search.trim()) {
        onChange(search.trim(), search.trim())
        setIsOpen(false)
      }
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        className={inputClassName || defaultInputClasses}
        placeholder={placeholder}
        value={isOpen ? search : displayLabel || search}
        onChange={(e) => {
          setSearch(e.target.value)
          if (!isOpen) setIsOpen(true)
          debouncedSearch(e.target.value)
        }}
        onFocus={() => {
          setIsOpen(true)
          setSearch('')
          if (onSearch) onSearch('')
        }}
        onKeyDown={handleKeyDown}
      />
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md shadow-lg">
          {searching ? (
            <div className="px-3 py-2 text-xs text-[var(--text-muted)]">Searching...</div>
          ) : filtered.length > 0 ? (
            filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--bg-secondary)] transition-colors ${
                  opt.value === value ? 'text-primary font-medium bg-[var(--bg-secondary)]' : 'text-[var(--text-primary)]'
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(opt)}
              >
                {opt.label}
              </button>
            ))
          ) : search.trim() ? (
            <div className="px-3 py-2 text-xs text-[var(--text-muted)]">No results</div>
          ) : null}
        </div>
      )}
    </div>
  )
}
